import { classNames } from '@fullstory/util/fmt';
import { BasePlacement, VariationPlacement } from '@popperjs/core';
import React, { ComponentProps } from 'react';
import { identifyComponent } from '../../util';
import { ButtonBase } from '../ButtonBase';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import css from './index.module.scss';

type IconButtonSize = 'small' | 'large';
type IconButtonVariant = 'minimal' | 'outline';

export interface IconButtonProps extends React.ComponentProps<typeof ButtonBase> {
  testId?: string;
  /**
   * The name of the icon to use. Corresponds with the `name` prop of
   * the `Icon` component.
   */
  iconName: ComponentProps<typeof Icon>['name'];
  /**
   * Overridden icon color, use sparingly.
   *
   * @default is the default Icon's color prop.
   */
  iconColor?: ComponentProps<typeof Icon>['color'];
  /**
   * Text to show in popover to provide clarity on IconButton's action
   */
  title: string;
  /**
   * The placement of the popover which contains the title text.
   * Default is `bottom`.
   */
  tooltipPlacement?: BasePlacement | VariationPlacement;
  /**
   * The size of icon button to use, based on the following scale:
   * @default "small"
   */
  size?: IconButtonSize;
  /**
   * Variant of icon button to use. Defaults to "minimal" because that
   * is the most commonly used variant for icon buttons.
   * @default "minimal"
   */
  variant?: IconButtonVariant;
  /**
   * If `true`, the icon button will use an inverted color scheme. This
   * is useful when an icon button is rendered on a dark or colored
   * background.
   */
  inverse?: boolean;
  tooltipAsChild?: React.ComponentPropsWithoutRef<typeof Tooltip>['asChild'];
}

/**
 * The `Icon` is rendered at one size increment smaller than the `IconButton`
 * size – e.g. the "medium" `IconButton` is 24x24 but its `Icon` is 16x16.
 */
const iconSizeMap: Record<IconButtonSize, ComponentProps<typeof Icon>['size']> = {
  small: 16,
  large: 20,
};

/**
 * Icon Buttons act as a wrapper that allows Icons to function as a
 * button. They can take shape as 3 different variants.
 */
export const IconButton = React.forwardRef<HTMLElement, IconButtonProps>(
  (
    {
      testId = 'icon-button',
      title,
      iconName,
      iconColor,
      size = 'small',
      variant = 'minimal',
      disabled = false,
      inverse = false, // TODO(johnverrone): implement inverse styles
      tooltipPlacement = 'bottom',
      tooltipAsChild = false,
      ...rest
    },
    ref,
  ) => {
    // Instead of using the `title` attribute of the button, we should use the `aria-label` attribute.
    // `title` is not actually read out loud by skreen readers.
    const buttonAriaLabel = title;
    const iconSize = iconSizeMap[size];
    const variantClass = variant === 'outline' ? css[variant] : null;

    return (
      <Tooltip
        data-component="IconButton"
        placement={tooltipPlacement}
        tooltipContent={title}
        showDelay={300}
        persistDelay={0}
        offset={16}
        compact
        interactive={false}
        className={css.iconButtonTooltip}
        asChild={tooltipAsChild}
      >
        <ButtonBase
          aria-label={buttonAriaLabel}
          {...rest}
          {...identifyComponent('icon-button', rest)}
          ref={ref}
          className={classNames(rest.className, css.iconButton, variantClass, css[size], {
            [css.inverse]: inverse,
          })}
          disabled={disabled}
          testId={testId}
        >
          <Icon name={iconName} size={iconSize} color={iconColor} />
        </ButtonBase>
      </Tooltip>
    );
  },
);

IconButton.displayName = 'IconButton';
